import { useEffect, useRef } from 'react';

/**
 * Put the window back at the top when the screen changes under it.
 *
 * <h3>What it is for</h3>
 *
 * Surfaces that swap whole screens inside one route - Protect's rail, a
 * catalogue that opens a detail view - get no scroll reset from the browser,
 * because no navigation happened. Somebody far down one screen arrives partway
 * down the next, with its heading above the fold, and reads that as a surface
 * that rendered wrong.
 *
 * <h3>Why this is shared</h3>
 *
 * The first surface to notice put `window.scrollTo(0, 0)` inside one handler,
 * and the handler beside it - entering the same screen a different way - did
 * not get one. One screen swap has one answer, and every entry into it has to
 * reach the same answer. Keying on what identifies the screen gets all of them
 * at once, including the ones nobody has written yet.
 *
 * <h3>What it deliberately does not do</h3>
 *
 * It does not scroll on mount. A page can be entered on a deep link, or with a
 * position the browser restored, and moving somebody at that moment takes them
 * away from what they came back to. It also does not scroll on a re-render that
 * did not change the key, which is what makes a long screen readable while
 * anything on it is still updating.
 *
 * @param key what identifies the screen. A string or number; compose it when
 *   the screen alone is not the whole identity - `view + ':' + agentId`, so
 *   opening a different agent on the same screen counts as a change.
 */
export default function useScrollTopOnChange(key) {
  /* The key at mount, so the first effect has nothing to compare against and
     nothing to do. */
  const previous = useRef(key);

  useEffect(() => {
    if (previous.current === key) return;
    previous.current = key;
    toTop();
  }, [key]);
}

/**
 * The scroll itself, instant.
 *
 * <p>A screen swap is not a movement within a page, so animating it is a
 * second thing happening at the same time as the new screen arriving. Instant
 * is also what the reduced-motion preference asks for, without having to ask
 * for it.</p>
 *
 * <p>Guarded twice: a window with no `scrollTo` at all (jsdom, a server
 * render), and one that takes only coordinates and rejects the options
 * form.</p>
 */
function toTop() {
  if (typeof window === 'undefined' || typeof window.scrollTo !== 'function') return;
  try {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  } catch {
    try { window.scrollTo(0, 0); } catch { /* nothing to scroll */ }
  }
}
